import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { roleLabel as roleLabelOf } from '../auth/access';
import { useAuth } from '../auth/AuthProvider';
import { GROUP_LABEL, GROUP_ORDER, GROUPING_THRESHOLD, visibleNavItems, type NavGroup } from '../lib/navItems';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { CommandPalette } from './CommandPalette';
import { AppMark } from './Logo';

const EXPANDED_KEY = 'rat.nav.expanded';

/** Los grupos arrancan CERRADOS (pedido explicito); esto solo guarda los que la persona abrio a mano. */
function loadExpanded(): Partial<Record<NavGroup, boolean>> {
  try {
    return JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? '{}');
  } catch {
    return {};
  }
}

/**
 * Sin selector de rol (D13): el rol y la vista se derivan del usuario autenticado.
 * Recortado respecto al AppShell real: los enlaces a auditoria, reporte,
 * importacion, reportes de bugs y administracion se retiraron porque esas
 * paginas no existen en este slice del backend -- ver router.tsx. El boton
 * de bug report tambien se retiro (dependia de una Edge Function de Supabase).
 *
 * Tablero institucional, tablero de unidad, seguimiento del levantamiento,
 * catalogos, usuarios y auditoria SI se reconectaron: el backend ya los
 * sostiene (dashboards.ts, tracking.ts, catalog-admin.ts, admin.ts,
 * audit.ts). "Catálogos" es nuevo de verdad -- ni el sistema original tuvo
 * esa pantalla. "Usuarios" existia como backend puro
 * (setInstitutional/assignUserToUnit, con su anti-autoelevacion ya
 * probada) pero sin rutas HTTP ni UI hasta esa pasada. "Auditoría" existia
 * solo como /audit/verify (un veredicto, sin lista de eventos) hasta esta
 * pasada (ver README del backend).
 * El ciclo de revision del DPD (observaciones, aprobar/observar) no tiene
 * enlace propio -- vive dentro de ActivityDetail cuando la actividad esta
 * EN_REVISION, no hace falta una "bandeja" separada porque ActivitiesList ya
 * filtra por estado.
 */
export function AppShell() {
  const { authz, access, session, signOut } = useAuth();
  // Rol REAL (Revisor RAT, Consulta, ...) segun las asignaciones del backend; antes se adivinaba a partir de permisos inventados.
  const roleLabel = roleLabelOf(access);
  const myUnitId = (authz.units.find((u) => u.unit_role === 'jefe') ?? authz.units[0])?.unit_id;
  const loc = useLocation();

  const items = useMemo(() => visibleNavItems(access, authz), [access, authz]);
  // Agrupar solo pasado el umbral (pedido explicito: "5 o mas"). Con pocos items (Jefe, Revisor)
  // el sidebar se queda plano, exactamente como estaba antes de esto -- ver lib/navItems.ts.
  const grouped = items.length >= GROUPING_THRESHOLD;

  const [expanded, setExpanded] = useState(loadExpanded);
  const toggleGroup = (g: NavGroup) => {
    setExpanded((prev) => {
      const next = { ...prev, [g]: !prev[g] };
      localStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
      return next;
    });
  };

  const paletteItems = useMemo(() => {
    const base = items.map((it) => ({ key: it.key, label: it.label, to: it.to, groupLabel: GROUP_LABEL[it.group] }));
    return myUnitId ? [{ key: 'mi-unidad', label: 'Mi unidad', to: `/unidad/${myUnitId}` }, ...base] : base;
  }, [items, myUnitId]);

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => setMenuOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="shell">
      <div
        className={`sidebar-scrim${menuOpen ? ' is-open' : ''}`}
        onClick={closeMenu}
        aria-hidden
      />

      <aside className={`sidebar${menuOpen ? ' is-open' : ''}`} id="nav-cajon">
        <div className="brand">
          <AppMark />
        </div>

        {/*
          Orden por prioridad (pedido explícito): lo personal primero (mi
          unidad, mi trabajo), después la vista amplia/de supervisión,
          después módulos operativos separados, y la administración al
          final -- patrón estándar de "yo -> equipo -> institución ->
          configuración". Mismo orden que sigue landingRouteFor() en
          router.tsx: unidad propia antes que el tablero institucional.
        */}
        <div className="grp">RAT</div>
        {myUnitId && (
          <NavLink to={`/unidad/${myUnitId}`} className={navcls} onClick={closeMenu}>Mi unidad</NavLink>
        )}
        {!grouped &&
          items.map((it) => (
            <NavLink key={it.key} to={it.to} end={it.end} className={navcls} onClick={closeMenu}>
              {it.label}
            </NavLink>
          ))}
        {grouped &&
          GROUP_ORDER.map((g) => {
            const groupItems = items.filter((it) => it.group === g);
            if (groupItems.length === 0) return null;
            const isOpen = expanded[g] === true;
            return (
              <div key={g}>
                <div
                  className="grp grp--toggle"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => toggleGroup(g)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGroup(g);
                    }
                  }}
                >
                  <span>{GROUP_LABEL[g]}</span>
                  <svg className="chev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {isOpen &&
                  groupItems.map((it) => (
                    <NavLink key={it.key} to={it.to} end={it.end} className={navcls} onClick={closeMenu}>
                      {it.label}
                    </NavLink>
                  ))}
              </div>
            );
          })}

        <div className="sidebar-foot">
          <span className="grp" style={{ padding: 0 }}>Tema</span>
          <ThemeToggle />
          {/* En móvil la sesión vive aquí: en la barra superior no cabía y la deformaba. */}
          <span className="sidebar-email mono">{session?.user.email}</span>
          <button className="btn sidebar-logout" onClick={() => void signOut()}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div>
        <div className="topbar">
          <button
            type="button"
            className="navtoggle"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="nav-cajon"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
          <span className="rolebadge">{roleLabel}</span>
          <div className="spacer" />
          <CommandPalette items={paletteItems} />
          <NotificationBell />
          <ThemeToggle />
          <span className="topbar-email mono">{session?.user.email}</span>
          <button className="btn btn--ghost topbar-logout" onClick={() => void signOut()}>
            Cerrar sesión
          </button>
        </div>
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const navcls = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : undefined);

